import {
  RiskFieldsTypeEnum,
  TemplateTypeEnum,
  useAllAssemblyTemplatesForFilterSideBarLazyQuery,
} from '@/__generated__/graphql';
import {
  allAssemblyTemplates,
  selectedAssemblyId,
  selectedAssemblyName as selectedAssemblyNameState,
  selectedAssemblyTemplateId,
  selectedRiskAssessmentFilters,
} from '@/components/Analysis/state';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors, primary } from '@/theme/colors';
import { AssemblyTemplatesQuery } from '@/types';
import { ArrowDropDown, Close, InfoOutlined } from '@mui/icons-material';
import { AccordionDetails, AccordionSummary, Fade, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { EquipmentDrawer } from '../EquipmentDrawer/EquipmentDrawer';
import { RiskAssessmentEquipmentTable } from '../RiskAssessmentEquipmentTable';
import { RiskAssessmentFilters } from '../RiskAssessmentFilters';
import {
  RiskAssessmentTabDetails,
  RiskAssessmentTabsHeader,
} from '../RiskAssessmentTabs/RiskAssessmentTabs';
import { RiskAssessmentDrawer } from '../RiskAssesssmentDrawer/RiskAssessmentDrawer';
import { VersionsDrawer } from '../VersionsDrawer';
import useDialogState from '../hooks/useDialogState';
import CommentDialog from '../shared/CommentDialog';
import * as state from '../state/atoms';
import {
  AccordianToggleButton,
  RiskAccordian,
  filterPaperStyles,
  riskAssessmentContainer,
  riskAssessmentStack,
  tabScreenStyles,
} from './styles';
import { MissingCommentsDialog } from '../MissingCommentsDialog';
import { TaskModal } from '../TaskModal';
import { BulkCommentsDialog } from '../BulkCommentsDialog';
import { ClearValuesDialog } from '../ClearValuesDialog';
import { DescriptionModal } from '../DescriptionModal';

type Props = {
  showVersionSidebar: boolean;
  setShowVersionSidebar: (argument0: boolean) => void;
  setShowEquipmentDrawer: (argument0: boolean) => void;
  isEsSheetPublished: boolean;
};

export const RiskAssessmentContainer = ({
  showVersionSidebar,
  setShowVersionSidebar,
  setShowEquipmentDrawer,
  isEsSheetPublished,
}: Props) => {
  const [showTransitionMessage, setShowTransitionMessage] = useState(false);
  const [selectedAssembly, setSelectedAssemblyId] = useAtom(selectedAssemblyId);
  const [selectedAssemblyName, setSelectedAssemblyName] = useAtom(selectedAssemblyNameState);
  const [selectedFilters, setSelectedFilters] = useAtom(selectedRiskAssessmentFilters);
  const [editorExpanded, setEditorExpanded] = useAtom(state.editorExpanded);
  const transitionPeriodCalculated = useAtomValue(state.transitionCalculated);
  const invalidDamageClassCountValue = useAtomValue(state.invalidDamageClassCount);
  const isOpen = useAtomValue(state.openState);
  const [selectedTab, setSelectedTab] = useAtom(state.selectedRiskTab);
  const setSelectedTemplateId = useSetAtom(selectedAssemblyTemplateId);
  const [allTemplates, setAllAssemblyTemplates] = useAtom(allAssemblyTemplates);
  const [isStructureTypeSelected, setIsStructureTypeSelected] = useState(false);
  const { resetDialogState, resetCommentState } = useDialogState();
  const { rightDrawerWidth } = useDrawerWidths();
  const structureId = useGetStructureId();
  const navbarHeight = useNavbarHeight();

  const [fetchAssemblyTemplatesLazy] = useAllAssemblyTemplatesForFilterSideBarLazyQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const doQuery = async () => {
      const { data } = await fetchAssemblyTemplatesLazy({
        variables: {
          structureId,
          type: TemplateTypeEnum.Assembly,
        },
      });
      const allTemplatesData = data?.allTemplates as AssemblyTemplatesQuery;
      setAllAssemblyTemplates(allTemplatesData);
      if (allTemplatesData?.length > 0) setSelectedTemplateId(allTemplatesData[0].id);
    };

    if (!allTemplates) {
      doQuery();
    }
  }, [
    allTemplates,
    fetchAssemblyTemplatesLazy,
    setAllAssemblyTemplates,
    setSelectedTemplateId,
    structureId,
  ]);

  useEffect(() => {
    return () => {
      setSelectedFilters([]);
    };
  }, [setSelectedFilters]);

  const handleTabClick = useCallback(
    (button: string) => {
      setEditorExpanded(true);
      setSelectedTab(button);
      setSelectedFilters(selectedFilters);
      resetDialogState();
    },
    [setEditorExpanded, setSelectedTab, setSelectedFilters, selectedFilters, resetDialogState]
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [hideContainer, setHideContainer] = useState(false);
  const [updateAllAssemblies, setUpdateAllAssemblies] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isStructureTypeSelected && selectedTab === '') {
      setSelectedTab(RiskFieldsTypeEnum.Pof);
    }
    if (!isStructureTypeSelected && selectedTab === RiskFieldsTypeEnum.Pof) {
      setSelectedTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStructureTypeSelected, selectedTab]);

  useEffect(() => {
    if (isEsSheetPublished) setSelectedTab(RiskFieldsTypeEnum.Review);
  }, [isEsSheetPublished, setSelectedTab]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerReference.current && !drawerReference.current.contains(event.target as Node)) {
        setDrawerOpen(false);
        setSelectedAssemblyId(undefined);
        setSelectedAssemblyName(undefined);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerReference]);

  const showEquipmentSideDrawer = useMemo(() => {
    return (
      Object.values(RiskFieldsTypeEnum)
        .filter((value) => value !== RiskFieldsTypeEnum.Review)
        .map((value) => value.toString())
        .includes(selectedTab) && !showVersionSidebar
    );
  }, [selectedTab, showVersionSidebar]);

  useEffect(() => {
    setShowEquipmentDrawer(showEquipmentSideDrawer);
  }, [setShowEquipmentDrawer, showEquipmentSideDrawer]);

  useEffect(() => {
    const shouldShowTransitionMessage =
      transitionPeriodCalculated && invalidDamageClassCountValue > 0;
    setShowTransitionMessage(shouldShowTransitionMessage);
  }, [invalidDamageClassCountValue, transitionPeriodCalculated]);

  const filtersState = useMemo(() => {
    return JSON.stringify(selectedFilters.filter((filter) => filter.id !== 'risk_material_type'));
  }, [selectedFilters]);

  useEffect(() => {
    setSelectedFilters((prev) => {
      const newFilters = prev.filter((filter) => filter.id !== 'risk_material_type');
      return [...newFilters];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState]);

  useEffect(() => {
    resetCommentState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState]);

  return (
    <>
      <MissingCommentsDialog />
      <BulkCommentsDialog />
      <ClearValuesDialog selectedTab={selectedTab} />
      <Stack
        spacing={3}
        sx={{
          ...riskAssessmentStack,
          maxWidth:
            showEquipmentSideDrawer || showVersionSidebar
              ? `calc(100% - ${rightDrawerWidth}px)`
              : '100%',
        }}
      >
        {!hideContainer && (
          <Box sx={riskAssessmentContainer}>
            <Stack direction="row" alignItems="center" width="100%">
              {selectedTab !== RiskFieldsTypeEnum.Review && (
                <Box
                  sx={{
                    maxWidth: { sm: '24rem', md: '28.8rem' },
                    width: '100%',
                    marginRight: '24px',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '1.6rem',
                      color: abyssColors.primary[500],
                    }}
                  >
                    Filters selection
                  </Typography>
                </Box>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
                gap={2}
              >
                {!isEsSheetPublished && (
                  <RiskAssessmentTabsHeader
                    selectedTab={selectedTab}
                    level1Selected={isStructureTypeSelected}
                    onTabClick={handleTabClick}
                  />
                )}
                {isEsSheetPublished && (
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '1.6rem',
                      color: '#425265',
                      marginBottom: '8px',
                    }}
                  >
                    Review Matrix
                  </Typography>
                )}
                <AccordianToggleButton
                  onClick={() => {
                    setEditorExpanded(!editorExpanded);
                  }}
                >
                  <ArrowDropDown
                    sx={{ transform: `rotate(${editorExpanded ? '180deg' : '0deg'})` }}
                  />
                </AccordianToggleButton>
              </Stack>
            </Stack>
            <RiskAccordian
              expanded={editorExpanded}
              elevation={0}
              slotProps={{
                transition: {
                  timeout: 300,
                },
              }}
              square
              disableGutters
            >
              <AccordionSummary
                id="panel-header"
                aria-controls="panel-content"
                sx={{ display: 'none' }}
              />
              <AccordionDetails>
                <Stack direction="row" spacing={3}>
                  {selectedTab !== RiskFieldsTypeEnum.Review && (
                    <Stack sx={{ maxWidth: { sm: '24rem', md: '28.8rem' }, width: '100%' }}>
                      <Paper elevation={0} sx={filterPaperStyles}>
                        <RiskAssessmentFilters
                          onStructureTypeSelected={setIsStructureTypeSelected}
                          isDisabled={selectedTab === RiskFieldsTypeEnum.Review}
                          filtersOrientation="column"
                          isStructureTypeSelected={isStructureTypeSelected}
                        />
                      </Paper>
                    </Stack>
                  )}
                  <Stack width="100%">
                    <Stack>
                      <Box>
                        <RiskAssessmentTabDetails
                          selectedTab={selectedTab}
                          updateAllAssemblies={updateAllAssemblies}
                          key={filtersState}
                        />
                      </Box>
                      <CommentDialog open={isOpen} />
                    </Stack>
                    {!isStructureTypeSelected && selectedTab === '' && (
                      <Paper elevation={0} sx={tabScreenStyles}>
                        <FeedbackCircle
                          backgroundColor={abyssColors.primary[50]}
                          diameter={400}
                          text={
                            <Typography
                              fontSize="1.4rem"
                              color={abyssColors.primary[300]}
                              lineHeight="normal"
                            >
                              Please select Level 1 and Level 2 component to proceed
                            </Typography>
                          }
                          icon={<AbyssIcon style={{ width: '4.4rem' }} iconName="table" />}
                        />
                      </Paper>
                    )}
                  </Stack>
                </Stack>
              </AccordionDetails>
            </RiskAccordian>
          </Box>
        )}

        <Box>
          {drawerOpen && (
            <RiskAssessmentDrawer
              setDrawerOpen={setDrawerOpen}
              ref={drawerReference}
              assemblyName={selectedAssemblyName}
              setSelectedAssemblyId={setSelectedAssemblyId}
              setSelectedAssemblyName={setSelectedAssemblyName}
            />
          )}
        </Box>
        {selectedTab === RiskFieldsTypeEnum.TransitionPeriod && showTransitionMessage && (
          <Fade in unmountOnExit>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                padding: '1.2rem',
                background: abyssColors.primary[400],
                borderRadius: '0.2rem',
                color: abyssColors.neutral[50],
              }}
            >
              <Stack direction="row" alignItems="center" gap="1.2rem">
                <InfoOutlined />
                <Typography>
                  The <span style={{ fontWeight: 700 }}>next transition period</span> cannot be
                  calculated for components without an assigned damage class (DC) value.
                </Typography>
              </Stack>
              <IconButton
                sx={{ padding: 0, color: abyssColors.neutral[50] }}
                onClick={() => {
                  setShowTransitionMessage(false);
                }}
              >
                <Close />
              </IconButton>
            </Stack>
          </Fade>
        )}
        <Box>
          {selectedTab !== '' && (
            <RiskAssessmentEquipmentTable
              selectedTab={selectedTab}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              setUpdateAllAssemblies={setUpdateAllAssemblies}
              setDrawerOpen={setDrawerOpen}
              updateAll={updateAllAssemblies}
              setHideContainer={setHideContainer}
            />
          )}
        </Box>
      </Stack>
      {showEquipmentSideDrawer && (
        <SideDrawer
          sxPaper={{
            background: primary.TintedBlue,
            marginTop: `${navbarHeight}px`,
            border: 0,
          }}
          position="right"
          width={rightDrawerWidth}
          isOpen={true}
          handleOpen={() => {}}
          scrollable
        >
          <EquipmentDrawer />
        </SideDrawer>
      )}
      {showVersionSidebar &&
        !showEquipmentSideDrawer &&
        selectedTab !== RiskFieldsTypeEnum.Review && (
          <VersionsDrawer
            isESVersion
            structureId={structureId}
            collectionName="Assembly"
            referenceId={selectedAssembly}
            onClose={() => {
              setShowVersionSidebar(false);
            }}
          />
        )}
      <TaskModal updateAllAssemblies={updateAllAssemblies} />
      <DescriptionModal />
    </>
  );
};
